/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PhoneNumbers.scss';

import classNames from 'classnames';
import { bool, func, object, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import FormattedInput from 'components/shared/FormattedInput/FormattedInput';
import ValidationDataRowLabel from 'components/layouts/Preferences/shared/ValidationDataRowLabel';
import {
  handleMultipleValidations,
  isValidNonUSPhoneNumber,
  isValidUsPhoneNumber
} from 'services/Validation';
import Store from 'state/Store';
import { touchedField } from 'state/validation/ValidationActions';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

const PhoneNumberWithPermissions = WithUserPermissions(FormattedInput);

export const USPhoneNumberRow = ({
  field,
  hasExt,
  label,
  onChange,
  disable,
  phoneNumbers,
  invalidFields,
  touchedFields,
  validator,
  validations
}) => {
  const fieldExt = `${field}Ext`;
  const phoneFormat = 'us';
  const phoneNumberValidatorOptions = {
    field,
    shouldValidate: touchedFields[field] === true,
    validator: (value) => handleMultipleValidations(value, validations[field]),
    value: phoneNumbers[field]
  };
  const phoneValidatorOptions = [phoneNumberValidatorOptions];

  if (hasExt) {
    const phoneNumberExtValidatorOptions = {
      field: field,
      shouldValidate: touchedFields[fieldExt] === true,
      validator: function (value) {
        const phoneNumberValue = phoneNumbers[field];
        const isValid =
          !value || (value && phoneNumberValue && validator(phoneNumberValue));
        return isValid || 'Invalid Phone Number';
      },
      value: phoneNumbers[fieldExt]
    };

    phoneValidatorOptions.push(phoneNumberExtValidatorOptions);
  }
  const onFieldBlur = () => Store.dispatch(touchedField(field));
  const onExtFieldBlur = () => Store.dispatch(touchedField(fieldExt));
  const onFieldChange = (field) => (value) => onChange(field, value);

  const fieldError =
    invalidFields[field] || invalidFields[fieldExt] || undefined;

  return (
    <div
      className={classNames('phone-number-row phone-number-row-us', {
        'field-in-error': fieldError
      })}>
      <ValidationDataRowLabel validators={phoneValidatorOptions}>
        {label}
      </ValidationDataRowLabel>
      <div className="phone-number-number-wrapper">
        <div className="phone-number-number">
          <label className="phone-number-digits-label phone-number-input-field">
            <PhoneNumberWithPermissions
              className="phone-number-input"
              format={phoneFormat}
              onBlur={onFieldBlur}
              onChange={onFieldChange(field)}
              disable={disable}
              value={phoneNumbers[field]}
              shouldMask
            />
          </label>
        </div>
        {hasExt && (
          <div className="phone-number-ext">
            <label className="phone-number-ext-label phone-number-input-field">
              <span className="phone-number-mobile-label">Ext.</span>
              <PhoneNumberWithPermissions
                className="phone-number-input number-ext"
                format="ext"
                onBlur={onExtFieldBlur}
                onChange={onFieldChange(fieldExt)}
                disable={disable}
                value={phoneNumbers[fieldExt]}
                shouldMask
              />
            </label>
          </div>
        )}
      </div>
      {fieldError ? (
        <span className="mobile-error-field">{fieldError}</span>
      ) : null}
    </div>
  );
};

USPhoneNumberRow.propTypes = {
  field: string.isRequired,
  hasExt: bool,
  label: string.isRequired,
  onChange: func.isRequired,
  phoneNumbers: object.isRequired,
  invalidFields: object.isRequired,
  touchedFields: object.isRequired,
  validator: func.isRequired
};

USPhoneNumberRow.defaultProps = {
  validator: isValidUsPhoneNumber
};

const NonUSPhoneNumberRow = ({
  field,
  label,
  onChange,
  disable,
  phoneNumbers,
  invalidFields,
  touchedFields
}) => {
  const fieldCountryCode = `${field}CountryCode`;
  const phoneFormat = 'non-us';
  const phoneValidatorOptions = [
    {
      field,
      shouldValidate: touchedFields[field] === true,
      validator: function (value) {
        const countryCodeValue = phoneNumbers[fieldCountryCode];
        const isValid = Boolean(
          (isValidNonUSPhoneNumber(value) && countryCodeValue) ||
            (!value && !countryCodeValue)
        );
        return isValid || 'Invalid Phone Number';
      },
      value: phoneNumbers[field]
    },
    {
      field: fieldCountryCode,
      shouldValidate:
        touchedFields[fieldCountryCode] === true ||
        touchedFields[field] === true,
      validator: function (value) {
        const phoneNumberValue = phoneNumbers[field];
        const isValid =
          !value ||
          (value &&
            phoneNumberValue &&
            isValidNonUSPhoneNumber(phoneNumberValue));
        return isValid || 'Invalid Phone Number';
      },
      value: phoneNumbers[fieldCountryCode]
    }
  ];
  const onCountryCodeFieldBlur = () =>
    Store.dispatch(touchedField(fieldCountryCode));
  const onFieldBlur = () => Store.dispatch(touchedField(field));
  const onFieldChange = (field) => (value) => onChange(field, value);

  const fieldError =
    invalidFields[field] || invalidFields[fieldCountryCode] || undefined;

  return (
    <div
      className={classNames('phone-number-row', {
        'field-in-error': fieldError
      })}>
      <ValidationDataRowLabel validators={phoneValidatorOptions}>
        {label}
      </ValidationDataRowLabel>
      <div className="phone-number-number-wrapper">
        <div className="phone-number-country-code phone-number-input-field">
          <label className="phone-number-country-code-label">
            <span className="phone-number-mobile-label">Country</span>
            <PhoneNumberWithPermissions
              className="phone-number-input number-country-code"
              dataTestId={fieldCountryCode}
              format="country-code"
              onBlur={onCountryCodeFieldBlur}
              onChange={onFieldChange(fieldCountryCode)}
              value={phoneNumbers[fieldCountryCode]}
              disable={disable}
              shouldMask
            />
          </label>
        </div>
        <div className="phone-number-number">
          <label className="phone-number-digits-label phone-number-input-field">
            <span className="phone-number-mobile-label">Phone Number</span>
            <PhoneNumberWithPermissions
              className="phone-number-input"
              format={phoneFormat}
              onBlur={onFieldBlur}
              onChange={onFieldChange(field)}
              disable={disable}
              value={phoneNumbers[field]}
              shouldMask
            />
          </label>
        </div>
      </div>
      {fieldError ? (
        <span className="mobile-error-field">{fieldError}</span>
      ) : null}
    </div>
  );
};

NonUSPhoneNumberRow.propTypes = {
  field: string.isRequired,
  label: string.isRequired,
  onChange: func.isRequired,
  phoneNumbers: object.isRequired,
  invalidFields: object.isRequired,
  touchedFields: object.isRequired
};

const PhoneNumberRow = ({
  addressType,
  field,
  hasExt,
  label,
  onChange,
  disable,
  phoneNumbers,
  invalidFields,
  touchedFields,
  validations
}) => {
  return addressType === 'non-us' ? (
    <NonUSPhoneNumberRow
      field={field}
      label={label}
      onChange={onChange}
      phoneNumbers={phoneNumbers}
      invalidFields={invalidFields}
      touchedFields={touchedFields}
      disable={disable}
    />
  ) : (
    <USPhoneNumberRow
      field={field}
      hasExt={hasExt}
      label={label}
      onChange={onChange}
      phoneNumbers={phoneNumbers}
      invalidFields={invalidFields}
      touchedFields={touchedFields}
      disable={disable}
      validations={validations}
    />
  );
};

PhoneNumberRow.propTypes = {
  addressType: string.isRequired,
  field: string.isRequired,
  hasExt: bool,
  label: string.isRequired,
  onChange: func.isRequired,
  phoneNumbers: object.isRequired,
  invalidFields: object.isRequired,
  touchedFields: object.isRequired
};

PhoneNumberRow.defaultProps = {
  hasExt: false
};

const mapStateToProps = (state) => ({
  invalidFields: state.invalidFields,
  touchedFields: state.touchedFields
});

export default connect(mapStateToProps)(PhoneNumberRow);